// @ts-strict-ignore
import {Section} from 'common/components/items-menu/types';
import {ProjectFeatureFlag, REPORT_BASICS_LINKS, ReportName, UNIQUE_PROJECTS} from 'common/report/constants';
import {Translatable} from 'common/i18n';
import {events} from 'common/report/util/settings-tracking';

import {HelpOnboardingSectionItemValue, NavReportConfig} from './types';

// make sure these values are in sync with common.styl's $chrome-header-height(s)
export const chromeHeaderPrimaryHeight = 60;
const chromeHeaderSecondaryHeight = 50;
export const chromeHeaderHeight = chromeHeaderPrimaryHeight + chromeHeaderSecondaryHeight;
export const DARK_MODE_KEY = `darkModeOn`;
export const LAST_USER_PROJECT_KEY = `lastUserProjectId`;

// for types of credit state, see BillingCredit django model's def state()
export enum CreditState {
  Valid = `valid`,
  Redeemed = `redeemed`,
  Expired = `expired`,
  NotEffective = `not_effective`,
}

export enum HelpLabels {
  AskQuestions = `ask-questions`,
  CommunityWidget = `community-faqs`,
  ContactUs = `contact-us`,
  ContactSales = `contact-sales`,
  ContactSupport = `contact-support`,
  DemoProject = `demo-projects`,
  Docs = `documentation`,
  SubmitFeedback = `submit-feedback`,
  GettingStarted = `getting-started`,
  Intro = `quick-start`,
  LiveChat = `live-chat`,
  ProductUpdates = `changelog`,
  Community = `visit-community`,
  HireAnExpert = `hire-an-expert`,
  YourPrivacyRights = `your-privacy-rights`,
  OnboardingGuide = `onboarding-guide`,
}

export const HelpLabelTranslatables: Record<HelpLabels, Translatable> = {
  [HelpLabels.AskQuestions]: ({t}) => t(`help.ask-questions`),
  [HelpLabels.CommunityWidget]: ({t}) => t(`help.community-faqs`),
  [HelpLabels.ContactUs]: ({t}) => t(`help.contact-us`),
  [HelpLabels.ContactSales]: ({t}) => t(`help.contact-sales`),
  [HelpLabels.ContactSupport]: ({t}) => t(`help.contact-support`),
  [HelpLabels.DemoProject]: ({t}) => t(`help.demo-projects`),
  [HelpLabels.Docs]: ({t}) => t(`help.documentation`),
  [HelpLabels.SubmitFeedback]: ({t}) => t(`help.submit-feedback`),
  [HelpLabels.GettingStarted]: ({t}) => t(`help.getting-started`),
  [HelpLabels.Intro]: ({t}) => t(`help.quick-start`),
  [HelpLabels.LiveChat]: ({t}) => t(`help.live-chat`),
  [HelpLabels.ProductUpdates]: ({t}) => t(`help.changelog`),
  [HelpLabels.Community]: ({t}) => t(`help.visit-community`),
  [HelpLabels.HireAnExpert]: ({t}) => t(`help.hire-an-expert`),
  [HelpLabels.YourPrivacyRights]: ({t}) => t(`help.your-privacy-rights`),
  [HelpLabels.OnboardingGuide]: ({t}) => t(`help.onboarding-guide`),
};

export enum HelpTrackingEvent {
  CommunityWidget = `[Help Widget] Community Widget`,
  ContactSales = `[Help Widget] Contact Sales`,
  Docs = `[Help Widget] Documentation`,
  ExternalLink = `[Help Widget] External Link`,
  Intro = `[Help Widget] Learn Mixpanel Basics`,
  SetupGuide = `[Help Widget] Setup Guide`,
  GettingStarted = `[Help Widget] Getting Started`,
  WhatsNew = `[Help Widget] What's New`,
  LiveChat = `[Help Widget] Live Chat Started`,
  Community = `[Help Widget] Visit Community`,
  HireAnExpert = `[Help Widget] Hire an Expert`,
  Support = `[Help Widget] Support`,
  OnboardingGuide = `[Help Widget] Onboarding Guide`,
  DocsLink = `[Help Widget] Clicked Docs`,
}

export enum HelpGettingStartedLabels {
  SetupGuide = `Setup Guide`,
  Dashboards = `Boards`,
  Insights = `Insights`,
  Funnels = `Funnels`,
  Flows = `Flows`,
  Retention = `Retention`,
}

export enum HelpSectionItemType {
  GettingStarted = `GettingStarted`,
  ProductUpdate = `ProductUpdate`,
}

export const CONTACT_US_SECTIONS = [];

export const HELP_GETTING_STARTED_SECTIONS: Section<HelpOnboardingSectionItemValue> = {
  items: [
    {
      label: HelpLabels.Intro,
      icon: `brand-mixpanel`,
    },
    {
      label: HelpGettingStartedLabels.SetupGuide,
      icon: `setup-guide`,
    },
    {
      label: HelpGettingStartedLabels.Dashboards,
      icon: `brand-dashboard`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Dashboards],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Insights,
      icon: `brand-insights`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Insights],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Funnels,
      icon: `brand-funnels`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Funnels],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Flows,
      icon: `brand-flows`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Flows],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Retention,
      icon: `brand-retention`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Retention],
        type: HelpSectionItemType.GettingStarted,
      },
    },
  ],
};

export const FULL_CHANGELOG_LINK = `https://docs.mixpanel.com/changelogs`;

export enum NavCategoryType {
  Analysis = `Analysis`,
  Applications = `Applications`,
  Dashboards = `Boards`,
  DataManagement = `Data Management`,
  Events = `Events`,
  Project = `Project`,
  Settings = `Settings`,
  Users = `Users`,
}

// remove this and fill this out in REPORT_INFO for the report if it is used in full display in the primary nav
const UNUSED_NAVIGATION_REPORT_INFO = {
  description: ``,
  icon: ``,
  label: ``,
};

export const REPORT_INFO: {[key in ReportName]?: NavReportConfig} = {
  [ReportName.Dashboards]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `dashboards`,
    endpointAliases: [`dashboard`],
    category: NavCategoryType.Dashboards,
  },
  [ReportName.Insights]: {
    description: ({t}) => t(`nav-header.insights-description`),
    endpoint: () => `insights`,
    icon: `brand-insights`,
    label: ({t}) => t(`mixology.app.insights`),
    category: NavCategoryType.Analysis,
  },
  [ReportName.Funnels]: {
    description: ({t}) => t(`nav-header.funnels-description`),
    icon: `brand-funnels`,
    label: ({t}) => t(`mixology.app.funnels`),
    endpoint: () => `funnels`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Flows]: {
    description: ({t}) => t(`nav-header.flows-description`),
    icon: `brand-flows`,
    label: ({t}) => t(`mixology.app.flows`),
    endpoint: () => `flows`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Retention]: {
    description: ({t}) => t(`nav-header.retention-description`),
    icon: `brand-retention`,
    label: ({t}) => t(`mixology.app.retention`),
    endpoint: () => `retention`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Signal]: {
    description: ({t}) => t(`nav-header.signal-description`),
    icon: `brand-signal`,
    label: ({t}) => t(`mixology.app.signal`),
    endpoint: () => `signal`,
    category: NavCategoryType.Applications,
  },
  [ReportName.Impact]: {
    description: ({t}) => t(`nav-header.impact-description`),
    icon: `brand-impact`,
    label: ({t}) => t(`mixology.app.impact`),
    endpoint: () => `impact`,
    category: NavCategoryType.Applications,
  },
  [ReportName.ExperimentReporting]: {
    beta: true,
    description: ({t}) => t(`nav-header.experiments-description`),
    icon: `brand-experiments`,
    label: ({t}) => t(`mixology.app.experiments`),
    endpoint: () => `experiment-reporting`,
    category: NavCategoryType.Applications,
    hidden: (reportContext): boolean => {
      return [
        UNIQUE_PROJECTS.Uber, // Uber
        UNIQUE_PROJECTS.UberEats, // Uber-Eats
      ].includes(reportContext.projectId?.toString());
    },
  },
  [ReportName.Users]: {
    description: ({t}) => t(`nav-header.users-description`),
    icon: `brand-explore`,
    label: ({t}) => t(`mixology.app.users`),
    endpoint: () => `users`,
    endpointAliases: [`profile`],
    category: NavCategoryType.Users,
  },
  [ReportName.Profile]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `profile`,
    category: NavCategoryType.Users,
  },
  [ReportName.Lexicon]: {
    description: ({t}) => t(`nav-header.lexicon-description`),
    icon: `brand-lexicon`,
    label: ({t}) => t(`mixology.app.lexicon`),
    endpoint: () => `lexicon`,
    category: NavCategoryType.DataManagement,
  },
  [ReportName.DataAudit]: {
    description: ({t}) => t(`nav-header.data-audit-description`),
    icon: `brand-audit`,
    label: ({t}) => t(`mixology.app.data-audit`),
    beta: true,
    endpoint: () => `audit`,
    hidden: (reportContext): boolean =>
      !(
        reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.ShowReportDataAudit) &&
        reportContext.isProjectOrGlobalWorkspace
      ),
    category: NavCategoryType.DataManagement,
  },
  [ReportName.Events]: {
    description: ({t}) => t(`nav-header.events-description`),
    endpoint: () => `events`,
    icon: `brand-liveview`,
    label: ({t}) => t(`mixology.app.events`),
    category: NavCategoryType.Events,
  },
  [ReportName.Cohorts]: {
    description: ({t}) => t(`nav-header.cohorts-description`),
    icon: `brand-cohorts`,
    label: ({t}) => t(`mixology.app.cohorts`),
    endpoint: (): string => `entity-management#~(entityType~'cohorts)`,
    category: NavCategoryType.DataManagement,
  },
  [ReportName.Integrations]: {
    description: ({t}) => t(`nav-header.integrations-description`),
    icon: `brand-integrations`,
    label: ({t}) => t(`mixology.app.integrations`),
    endpoint: () => `integrations`,
    disabling: {
      isDisabled: (reportContext) => !reportContext.isProjectOrGlobalWorkspace,
      disabledMsg: ({t}) => t(`nav-header.integrations-disabled`),
    },
    category: NavCategoryType.DataManagement,
  },
  [ReportName.JqlConsole]: {
    label: ({t}) => t(`mixology.app.jql`),
    description: ({t}) => t(`nav-header.jql-description`),
    icon: ``,
    endpoint: () => `jql-console`,
    // JQL would allow users to bypass data filters, it should be hidden for workspaces
    hidden: (reportContext): boolean => reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportJqlConsole),
    disabling: {
      isDisabled: (reportContext) => !reportContext.isProjectOrGlobalWorkspace,
      disabledMsg: ({t}) => t(`nav-header.jql-disabled`),
    },
    category: NavCategoryType.Applications,
  },
  [ReportName.Revenue]: {
    label: ({t}) => t(`mixology.app.revenue`),
    description: ({t}) => t(`nav-header.revenue-description`),
    icon: ``,
    endpoint: () => `revenue`,
    hidden: (reportContext): boolean =>
      reportContext.projectId > 2845000 || // hide revenue for all new projects
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportRevenue) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: NavCategoryType.Applications,
  },
  [ReportName.UserSettings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings/account`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Settings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Setup]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `setup`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Views]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `views`,
    category: NavCategoryType.Project,
  },
};

export const SETTINGS_CATEGORY_TRACKING = {
  Org: events.viewOrg,
  Account: events.viewAccountSettings,
  Project: events.viewProjectSettings,
  Workspace: events.viewWorkspaceSettings,
  GettingStarted: events.setupMixpanel,
  BillingDetails: events.billingDetails,
  StartupCreditPromoClicked: events.clickStartupPromoLink,
  InviteModal: events.inviteModalOpen,
  AdminOrg: events.viewAdminOrgSettings,
};
